import { memo } from 'react';
import type { AdminUserItem, PaymentModeValue } from './adminUsers.api';
import { PROVIDER_DISPLAY_NAMES } from '../admin/admin.api'; // task-36: shared display names — single source of truth
import AdminNav from '../admin/components/AdminNav';
import './AdminUsers.css';

interface Props {
  loading: boolean;
  error: string | null;
  users: AdminUserItem[];
  onChangeRole: (id: string, role: 'admin' | 'user') => void;
  onChangePaymentMode: (id: string, paymentMode: PaymentModeValue) => void;
  apiKeyMissing?: boolean;
  onCreate: (email: string, password: string) => void;
  onDelete: (id: string) => void;
  supportedProviders?: string[]; // task-36: backend-sourced list; undefined while loading → selector stays empty/disabled
}

const AdminUsersPageView = memo(function AdminUsersPageView({ loading, error, users, onChangeRole, onChangePaymentMode, apiKeyMissing, onCreate, onDelete, supportedProviders = [] }: Props) { // task-36: default to empty array so map() never throws
  if (loading) return <main className="admin-users-container" role="status" aria-live="polite">Loading users…</main>;
  if (error) return <main className="admin-users-container admin-error-text" role="alert">Error: {error}</main>;

  return (
    <main className="admin-users-container" role="status" aria-live="polite">
      <AdminNav />
      <div className="admin-users-content">
        {apiKeyMissing && (
          <div className="admin-api-key-warning">
            Users routes require an API key. Set VITE_QreditCharge_API_KEY or localStorage.QreditCharge_API_KEY and refresh.
          </div>
        )}
        <h1 className="admin-users-title">Admin Users</h1>

        <form className="admin-users-form" onSubmit={(e) => {
          e.preventDefault();
          const fd = new FormData(e.currentTarget as HTMLFormElement);
          const email = String(fd.get('email') || '');
          const password = String(fd.get('password') || '');
          if (email && password) onCreate(email, password);
          (e.currentTarget as HTMLFormElement).reset();
        }}>
          <label htmlFor="email-input" className="">Email</label>
          <input id="email-input" name="email" type="email" placeholder="email" required className="admin-users-input" />
          <label htmlFor="password-input" className="">Password</label>
          <input id="password-input" name="password" type="password" placeholder="password" required className="admin-users-input" />
          <button type="submit" className="admin-users-button">Create</button>
        </form>
        <table className="admin-users-table">
          <thead>
            <tr>
              <th scope="col" className="admin-users-th">Email</th>
              <th scope="col" className="admin-users-th">Role</th>
              <th scope="col" className="admin-users-th">Created</th>
              <th scope="col" className="admin-users-th">Last Login</th>
              <th scope="col" className="admin-users-th">Payment Mode</th>
              <th scope="col" className="admin-users-th">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id}>
                <td className="admin-users-td">{u.email}</td>
                <td className="admin-users-td">{u.role}</td>
                <td className="admin-users-td">{new Date(u.createdAt).toLocaleString()}</td>
                <td className="admin-users-td">{u.lastLogin ? new Date(u.lastLogin).toLocaleString() : '-'}</td>
                <td className="admin-users-td">
                  <label htmlFor={`payment-mode-select-${u.id}`} className="visually-hidden">Payment Mode</label>
                  <select
                    id={`payment-mode-select-${u.id}`}
                    aria-label={`Payment mode for ${u.email}`}
                    value={u.paymentMode ?? ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      // task-36: guard uses the dynamic list — no hard-coded provider names here
                      if (supportedProviders.includes(v)) onChangePaymentMode(u.id, v as PaymentModeValue);
                    }}
                  >
                    {!u.paymentMode && <option value="" disabled>(default)</option>}
                    {/* task-36: .map() over the backend-sourced list so a new provider only needs localVars.ts updating */}
                    {supportedProviders.map((p) => (
                      <option key={p} value={p}>{PROVIDER_DISPLAY_NAMES[p] ?? p}</option>
                    ))}
                  </select>
                </td>
                <td className="admin-users-td">
                  <label htmlFor={`role-select-${u.id}`} className="">Role</label>
                  <select id={`role-select-${u.id}`} defaultValue={u.role} onChange={(e) => onChangeRole(u.id, e.target.value as 'admin' | 'user')}>
                    <option value="user">user</option>
                    <option value="admin">admin</option>
                  </select>
                  <button 
                    type="button" 
                    onClick={() => onDelete(u.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onDelete(u.id);
                      }
                    }}
                    className="admin-users-delete-button"
                    aria-label={`Delete user ${u.email}`}
                  >Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </main>
  );
});

export default AdminUsersPageView;
